import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { DiagnosisStepChip } from "@/components/diagnosis/DiagnosisStepChip";
import { layoutTokens, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";

import reviewBackIcon from "@assets/image/property_registration/review-back.svg";

import {
  PropertyRegistrationMode,
  RegistrationContext,
  RegistrationFlow,
  RegistrationStepContext,
} from "./RegistrationContext";

const useStyles = makeStyles(() => ({
  wrap: {
    width: "100%",
    maxWidth: 560,
    margin: "0 auto",
    [theme.breakpoints.up("md")]: { marginTop: layoutTokens.space[32], marginBottom: layoutTokens.space[64] },
  },
  pageTitle: { ...textStyles["PC/Heading/H2"], color: theme.palette.semantic["text/heading"], marginBottom: 16 },
  card: {
    padding: layoutTokens.space[24],
    minHeight: "calc(100vh - 56px)",
    backgroundColor: theme.palette.semantic["surface/card"],
    [theme.breakpoints.up("md")]: { minHeight: 0, borderRadius: layoutTokens.radius.lg, padding: "56px 40px 64px" },
  },
  reviewCard: { "&&": { paddingBottom: 0 } },
  stepper: {
    display: "flex",
    gap: layoutTokens.space[8],
    marginBottom: layoutTokens.space[32],
    "& > div": { flex: 1, minWidth: 0, justifyContent: "center", padding: "8px 4px" },
  },
  head: { marginBottom: layoutTokens.space[32] },
  notice: { marginBottom: layoutTokens.space[24] },
  title: { ...textStyles["SP/Heading/H2"] },
  description: {
    ...textStyles["SP/Body/Small"],
    color: theme.palette.text.secondary,
    marginTop: 8,
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"] },
  },
  form: {
    "& [data-registration-content]": { width: "100%", margin: 0 },
    "& .MuiGrid-container": { width: "100%", margin: 0 },
    "& .MuiGrid-item": { maxWidth: "100%", flexBasis: "100%", padding: 0 },
    "& [data-registration-section], & h4": { ...textStyles["SP/Heading/H4"], marginTop: 32, marginBottom: 8 },
    "& [data-registration-content] > [data-registration-section]:first-child": { marginTop: 0 },
    "& [data-registration-group]": { background: "none", padding: 0 },
    "& .MuiTypography-body2": { fontSize: 12 },
    "& [data-registration-upload] > div": {
      backgroundColor: theme.palette.semantic["surface/base"],
      borderRadius: 8,
      padding: 16,
      alignItems: "center",
      textAlign: "center",
    },
    "& [data-registration-upload] h5": { display: "none" },
    "& [data-registration-upload] label": { width: "100%", order: 1 },
    "& [data-registration-upload] .MuiTypography-body2": { order: 2, color: theme.palette.text.secondary },
    "& [data-registration-upload] .MuiButton-root": {
      border: 0,
      width: "100%",
      borderRadius: 9999,
      fontWeight: 700,
      color: theme.palette.text.primary,
      minHeight: 48,
    },
    [theme.breakpoints.up("md")]: {
      "& [data-registration-section], & h4": { ...textStyles["PC/Heading/H4"] },
      "& [data-registration-field] button": { fontSize: 16 },
    },
  },
  optionList: { display: "flex", flexDirection: "column", gap: 16 },
  option: {
    width: "100%",
    border: `1px solid ${theme.palette.semantic["border/default"]}`,
    borderRadius: layoutTokens.radius.lg,
    padding: layoutTokens.space[20],
    backgroundColor: theme.palette.semantic["surface/card"],
    boxShadow: "0 2px 8px rgba(22,32,46,0.06)",
    color: theme.palette.text.primary,
    textAlign: "left",
    cursor: "pointer",
    font: "inherit",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  optionTitle: { display: "flex", alignItems: "center", gap: 8, ...textStyles["SP/Heading/H3"] },
  badge: {
    backgroundColor: theme.palette.semantic["accent/bg"],
    color: theme.palette.semantic["accent/text"],
    borderRadius: layoutTokens.radius.full,
    padding: "4px 8px",
    ...textStyles["SP/Heading/H5"],
  },
  chevron: { marginLeft: "auto" },
}));

export const RegistrationLayout: React.FC<{
  onBack: () => void;
  step?: number;
  building?: boolean;
  flow?: RegistrationFlow;
  title: string;
  description?: string;
  review?: boolean;
  notice?: React.ReactNode;
}> = ({ onBack, step, building, title, description, children, flow, review = false, notice }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const labels = building ? ["建物", "ローン", "部屋", "経費"] : ["物件概要", "価格・ローン", "毎月の収支"];
  return (
    <BaseTemplate
      headerTitle={isMobile ? "物件を登録" : undefined}
      onBack={isMobile ? onBack : undefined}
      HeaderBackIconComponent={review ? <img src={reviewBackIcon} alt="" aria-hidden="true" data-review-back /> : undefined}
      isDense
      isPcV2
      disableBottomPadding
    >
      <Box className={classes.wrap}>
        {!isMobile && (
          <>
            <Box width={80}>
              <CustomButton
                type="button"
                tone="text"
                fullWidth={false}
                onClick={onBack}
                buttonSize="S"
                style={{ margin: "0 0 8px", padding: 0, minWidth: 0, whiteSpace: "nowrap", gap: 4 }}
              >
                <Chevron direction="back" /> 戻る
              </CustomButton>
            </Box>
            <Typography component="h1" className={classes.pageTitle}>
              物件を登録
            </Typography>
          </>
        )}
        <RegistrationContext.Provider value>
          <RegistrationStepContext.Provider value={{ flow: flow || (building ? "building" : "own"), step }}>
            <Box className={`${classes.card} ${review ? classes.reviewCard : ""}`}>
              {notice && <Box className={classes.notice}>{notice}</Box>}
              {step !== undefined && (
                <Box className={classes.stepper} aria-label="登録の進み具合">
                  {labels.map((label, index) => (
                    <DiagnosisStepChip
                      key={label}
                      v2
                      label={label}
                      withCheck={index < step}
                      state={index < step ? "completed" : index === step ? "active" : "upcoming"}
                    />
                  ))}
                </Box>
              )}
              {!review && <Box className={classes.head}>
                <Typography component="h2" className={classes.title}>
                  {title}
                </Typography>
                {description && <Typography className={classes.description}>{description}</Typography>}
              </Box>}
              <Box className={classes.form}>{children}</Box>
            </Box>
          </RegistrationStepContext.Provider>
        </RegistrationContext.Provider>
      </Box>
    </BaseTemplate>
  );
};

export const RegistrationMethods: React.FC<{ onSelect: (mode: PropertyRegistrationMode) => void }> = ({ onSelect }) => {
  const classes = useStyles();
  return (
    <Box className={classes.optionList}>
      <button type="button" className={classes.option} onClick={() => onSelect("ai")}>
        <span className={classes.optionTitle}>
          AIにまかせる <span className={classes.badge}>おすすめ</span>
          <Chevron className={classes.chevron} />
        </span>
        <Typography component="span" className={classes.description} style={{ display: "block" }}>
          物件の概要から価格・ローン・毎月の経費を推定します。内容を確認・修正してから登録できます。
        </Typography>
      </button>
      <button type="button" className={classes.option} onClick={() => onSelect("manual")}>
        <span className={classes.optionTitle}>
          詳細入力
          <Chevron className={classes.chevron} />
        </span>
        <Typography component="span" className={classes.description} style={{ display: "block" }}>
          売買契約書や返済予定表が手元にある方へ。契約・経費の内訳まで入力します。
        </Typography>
      </button>
      <Typography className={classes.description}>登録した情報は、物件詳細からいつでも変更できます。</Typography>
    </Box>
  );
};
